import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * What the operator currently has selected on the map. Drives the anchored
 * info popup (a single pin vs an aggregated cluster) and, via
 * {@link MapUiState.highlightedAlertId}, the "point-to" flash in the live feed.
 */
export type MapSelection =
  | { kind: 'alert'; alertId: string }
  | { kind: 'cluster'; ruleId: string; alertIds: string[] }
  | null;

interface MapUiState {
  selection: MapSelection;
  /** Alert to scroll-to + flash in the feed; cleared after the transient flash. */
  highlightedAlertId: string | null;
}

const initialState: MapUiState = { selection: null, highlightedAlertId: null };

const mapUiSlice = createSlice({
  name: 'mapUi',
  initialState,
  reducers: {
    selectAlert(state, action: PayloadAction<string | null>) {
      state.selection = action.payload ? { kind: 'alert', alertId: action.payload } : null;
      state.highlightedAlertId = action.payload;
    },
    selectCluster(state, action: PayloadAction<{ ruleId: string; alertIds: string[] }>) {
      state.selection = { kind: 'cluster', ...action.payload };
      // Point the feed at the first member of the cluster.
      state.highlightedAlertId = action.payload.alertIds[0] ?? null;
    },
    /** Point the feed at an alert without changing the open popup (used by the
     *  cluster popup's member cards). */
    highlightAlert(state, action: PayloadAction<string>) {
      state.highlightedAlertId = action.payload;
    },
    clearSelection(state) {
      state.selection = null;
      state.highlightedAlertId = null;
    },
    clearHighlight(state) {
      state.highlightedAlertId = null;
    },
  },
});

export const { selectAlert, selectCluster, highlightAlert, clearSelection, clearHighlight } =
  mapUiSlice.actions;
export default mapUiSlice.reducer;
